<template>

    <cms-panel class="complex-filter-panel" padding="false">
        <div class="filters">
            <div class="row" v-for="(filter, index) in filters" :key="index">
                <div class="dropdown-container">
                    <cms-dropdown :options="keyOptions" placeholder="Kies filter..."
                                  v-model="filter.key" :disabled="!!filter.key"></cms-dropdown>
                </div>
                <div class="dropdown-container">
                    <cms-dropdown :options="getOperatorOptions(filter.key)" :placeholder="filter.key ? 'Kies conditie...' : ''"
                                  v-model="filter.value.operator" :disabled="!filter.key || hasOneOperatorOption(filter.key)" @input="updateValue"></cms-dropdown>
                </div>
                <div class="dropdown-container">

                    <input v-if="getFilterType(filter.key) === 'string'" type="text" class="input-normal" placeholder="Waarde..."
                           v-model.trim="filter.value.value" @blur="updateValue" @keydown.enter="$event.target.blur()" />

                    <input v-else-if="getFilterType(filter.key) === 'number'" type="number" class="input-normal" placeholder="Waarde..."
                           v-model.trim="filter.value.value" @blur="updateValue" @keydown.enter="$event.target.blur()" />

                    <date-input v-else-if="getFilterType(filter.key) === 'date'" placeholder="Waarde..."
                                v-model.trim="filter.value.value" @blur="updateValue"/>

                    <cms-dropdown v-else :options="isFilterIncomplete(filter) ? getAvailableValueOptions(filter.key) : getValueOptions(filter.key)"
                                  :placeholder="filter.key ? 'Kies waarde...' : ''" v-model="filter.value.value"
                                  :disabled="!filter.key" searchable="true" @input="updateValue"></cms-dropdown>
                </div>
                <div class="row-actions">
                    <a href="#" @click.prevent="deleteFilter(filter)" v-if="!!filter.key"><i class="icon magicon-minus-circle-filled"></i></a>
                    <a href="#" @click.prevent="addFilter" v-if="!hasIncompleteFilter && index === filters.length-1"><i class="icon magicon-plus-circle-filled"></i></a>
                </div>
            </div>
        </div>
    </cms-panel>

</template>

<script>

import Component from 'vue-class-component'
import Vue from 'global-vue'

import find from 'lodash/find'
import without from 'lodash/without'
import isObject from 'lodash/isObject'
import filter from 'lodash/filter'
import pickBy from 'lodash/pickBy'
import isArray from 'lodash/isArray'
import groupBy from 'lodash/groupBy'
import clone from 'lodash/clone'
import difference from 'lodash/difference'
import first from 'lodash/first'

const STRING_OPERATORS = {
    'EQUALS': 'Is gelijk aan',
    'STARTS_WITH': 'Begint met',
    'STARTS_NOT_WITH': 'Begint niet met',
    'ENDS_WITH': 'Eindigt met',
    'ENDS_NOT_WITH': 'Eindigt niet met',
    'CONTAINS': 'Bevat',
    'NOT_CONTAINS': 'Bevat niet'
};

const VALUE_OPERATORS = {
    'EQUALS': 'Gelijk aan',
    'NOT_EQUALS': 'Niet gelijk aan',
    'GREATER_THAN': 'Groter dan',
    'GREATER_THAN_EQUALS': 'Groter dan of gelijk aan',
    'LESS_THAN': 'Kleiner dan',
    'LESS_THAN_EQUALS': 'Kleiner dan of geljk aan'
};

const DEFAULT_OPERATOR = "DEFAULT";

@Component({

    name: 'complex-filter-panel',
    props: ['options', 'value']
})
export default class extends Vue
{
    data(){

        return {
            filters: [this.getEmptyFilter()]
        }
    }

    created(){

        this.updateFromValue(false);
        this.$watch('value', () => this.updateFromValue());

        this.$watch('filters', () => this.onFiltersChange(), { deep: true });
    }

    get keyOptions(){

        return this.options.map(option => ({ label: option.title, value: option.key }));
    }

    get hasIncompleteFilter(){

        return !!this.filters.filter(filter => this.isFilterIncomplete(filter)).length;
    }

    onFiltersChange(){

        // Check filter with one operator option
        for(let filter of this.filters){

            if(filter.key && !filter.value.operator && this.hasOneOperatorOption(filter.key)){

                const options = Object.keys(this.getOperatorOptions(filter.key));
                filter.value.operator = first(options);
            }
        }
    }

    isFilterIncomplete(filter){

        return filter.key === null || filter.value.operator === null || filter.value.value === null;
    }

    getFilterType(key){

        const option = this.getOption(key);
        return option ? option.type : null;
    }

    getValueOptions(key){

        const option = this.getOption(key);
        return option ? option.options : [];
    }

    getAvailableValueOptions(key){

        let options = this.getValueOptions(key);

        const values = filter(this.filters, { key }).map(filter => filter.value);
        options = isArray(options) ? filter(options, option => values.indexOf(option.value) < 0) : pickBy(options, (label, option) => values.indexOf(option) < 0);

        return options;
    }

    getOperatorOptions(key){

        const option = this.getOption(key);
        if(!option){
            return [];
        }

        const type = option.type;

        if(type === 'string'){
            return STRING_OPERATORS;
        }
        else if(['value', 'number', 'date'].indexOf(type) >= 0){
            return VALUE_OPERATORS;
        }
        else {
            return {
                [DEFAULT_OPERATOR]: STRING_OPERATORS.EQUALS
            }
        }
    }

    hasOneOperatorOption(key){

        const options = this.getOperatorOptions(key);
        return Object.keys(options).length < 2;
    }

    updateFromValue(appendEmpty=true){

        if(!this.value){
            return;
        }

        const incompleteFilters = this.filters.filter(filter => this.isFilterIncomplete(filter));
        let filters = [];

        for(const [key, items] of Object.entries(this.value)){

            for(let filterValue of items){

                let translatedValue = clone(filterValue);

                if(!isObject(translatedValue)){

                    translatedValue = {
                        operator: DEFAULT_OPERATOR,
                        value: translatedValue
                    }
                }

                filters.push({
                    key, value: translatedValue
                });
            }
        }

        let allFilters = appendEmpty ? [...filters, ...incompleteFilters] : filters;

        if(!allFilters.length){
            allFilters.push(this.getEmptyFilter());
        }

        this.filters = allFilters;
    }

    updateValue(){

        let filters = {};

        const completeFilters = this.filters.filter(filter => !this.isFilterIncomplete(filter));
        const groupedFilters = groupBy(completeFilters, 'key');
        const filterKeys = Object.keys(groupedFilters);

        for(let key of filterKeys){

            const keyFilters = groupedFilters[key];
            filters[key] = keyFilters.map(({ value }) => value.operator === DEFAULT_OPERATOR ? value.value : clone(value));
        }

        const oldKeys = Object.keys(this.value || {});
        const newKeys = difference(filterKeys, oldKeys);

        this.$emit('input', filters);

        for(let key of newKeys) {
            this.$emit('add', { key, filters: filters[key] });
        }
    }

    addFilter(){

        if(!this.hasIncompleteFilter){
            this.filters.push(this.getEmptyFilter());
        }
    }

    deleteFilter(filter){

        this.filters = without(this.filters, filter);

        if(!this.filters.length){
            this.addFilter();
        }

        this.updateValue();
        this.$emit('remove', filter);
    }

    reset(){

        this.filters = [this.getEmptyFilter()];
        this.updateValue();
    }

    getEmptyFilter(){

        return {
            key: null,
            value: {
                operator: null,
                value: null
            }
        };
    }

    getOption(key){

        return key ? find(this.options, { key }) : null;
    }
}

</script>